type PyFormLike = {
	setFieldMeta: (
		fieldId: string,
		updater: (prev: Record<string, unknown> | undefined) => Record<string, unknown>
	) => void;
};

/**
 * Sets a server-side error on a PYForm field and marks the field as touched so the error
 * renders. Use this from imperative-handle bridges (e.g. cache-fast-path collision blocks)
 * where the form's normal `setError` callback isn't reachable from a click handler.
 *
 * Must be called against a pyForm obtained via `usePyForm()`.
 *
 * The errorMap bump is load-bearing: TanStack's useField only reactively subscribes to a
 * fixed set of meta keys (value, isTouched, isBlurred, isDirty, errorMap, errorSourceMap,
 * isValidating). `serverError` is not one of them, so setting it on an already-touched
 * field would not trigger a re-render and the inline error would not appear. Bumping
 * errorMap's reference forces the subscribed slice to change.
 */
export const setFieldServerError = (pyForm: PyFormLike, fieldId: string, message: string | undefined): void => {
	pyForm.setFieldMeta(fieldId, (prev) => {
		const prevErrorMap = (prev?.errorMap as Record<string, unknown> | undefined) ?? {};
		return {
			...prev,
			isTouched: true,
			serverError: message,
			errorMap: { ...prevErrorMap, __serverError: message },
		};
	});
};

/**
 * Removes a server-side error previously set by {@link setFieldServerError}.
 *
 * Call this before re-validating on a retry. `__serverError` is not a schema key, so a validation
 * pass never clears it — leaving it in the errorMap keeps the form invalid forever, which silently
 * dead-ends any submit gated on validity (e.g. a name-collision block that the user has since fixed).
 * The key is deleted rather than blanked so nothing downstream reads an empty-string error.
 */
export const clearFieldServerError = (pyForm: PyFormLike, fieldId: string): void => {
	pyForm.setFieldMeta(fieldId, (prev) => {
		const { __serverError, ...restErrorMap } = (prev?.errorMap as Record<string, unknown> | undefined) ?? {};
		return {
			...prev,
			serverError: undefined,
			errorMap: restErrorMap,
		};
	});
};
